import { ENTITLEMENT, InvitationStatus, SORT_OPTION } from 'enums';
import { SUBSCRIPTION_PLAN } from 'constants/billingInfo';
import { Team, TeamMember, TeamsMembers } from 'interfaces/team.interface';
import { Entitlement } from 'interfaces/initial-states.interface';
import {
  ActiveEntitlement,
  Subscription
} from 'interfaces/subscription.interface';
import moment from 'moment';
import { isUUID } from 'validator';
import {
  EMPTY_STRING,
  TOTAL_COMPLETION_CATEGORIES_90D
} from 'constants/general';
import { TEAM_COLUMN_TITLES } from 'constants/table';
import { toFixedNumber, toInt } from './helpers';
import { clamp } from './number.util';
import { isNowOrAfter, isTodayOnOrBefore } from './time.util';

const MAX_COMPLETION_PERCENT = 100;

export const getAvailableTeamSpots = (team: Team | null) => {
  if (!team) {
    return 0;
  }
  // Null limit = unlimited (Teacher Mode OFFLINE / class teams).
  if (team.team_size_limit == null) {
    return Number.POSITIVE_INFINITY;
  }
  return clamp(team.team_size_limit - team.team_size, 0, team.team_size_limit);
};

export const isTeamOwnerOrAdmin = (activeEntitlements: ActiveEntitlement[]) =>
  activeEntitlements.some((activeEntitlement) =>
    [ENTITLEMENT.TEAM_OWNER, ENTITLEMENT.TEAM_ADMIN].includes(
      activeEntitlement.entitlement
    )
  );

export const getSortedMembers = (
  admins: string[],
  members: TeamMember[],
  column?: string,
  sortType: SORT_OPTION = SORT_OPTION.ASC
) => {
  const direction = sortType === SORT_OPTION.DESC ? -1 : 1;

  const columnMapping: {
    [key: string]: (member: TeamMember) => string | number;
  } = {
    [TEAM_COLUMN_TITLES.NAME]: (member) => member.first_name?.toLowerCase(),
    [TEAM_COLUMN_TITLES.EMAIL]: (member) => member.email?.toLowerCase(),
    [TEAM_COLUMN_TITLES.INVITATION_STATUS]: (member) =>
      member.invitation_status?.toLowerCase(),
    [TEAM_COLUMN_TITLES.MEMBER_TYPE]: (member) =>
      member.id && admins.includes(member.id) ? 1 : 0,
    [TEAM_COLUMN_TITLES.HAS_ACCESS]: (member) =>
      member.member_expiry_date
        ? new Date(member.member_expiry_date).getTime()
        : 0,
    [TEAM_COLUMN_TITLES.PERCENT_DAYS_USED]: (member) =>
      member.avgRoutineFocusCompletion90d
  };

  if (!column || !columnMapping[column]) {
    return [...members];
  }

  const getColumnValue = columnMapping[column];

  return [...members].sort((a, b) => {
    const valA = getColumnValue(a);
    const valB = getColumnValue(b);

    if (valA < valB) {
      return -1 * direction;
    }
    if (valA > valB) {
      return 1 * direction;
    }
    return 0;
  });
};

export const checkSubscriptionValidity = (
  subscription?: Subscription | Entitlement
) => {
  const hasLifetimeEntitlement = subscription?.expires_date === null;
  const hasActiveSubscription =
    hasLifetimeEntitlement ||
    isNowOrAfter(subscription?.expires_date, 'second');
  const hasUnfinishedPersonalSubscriptionPeriod = [
    SUBSCRIPTION_PLAN.PERSONAL_MONTHLY,
    SUBSCRIPTION_PLAN.LIFE_TIME
  ].includes(subscription?.product_identifier ?? '')
    ? hasLifetimeEntitlement || isTodayOnOrBefore(subscription?.expires_date)
    : false;

  return {
    hasActiveSubscription,
    hasUnfinishedPersonalSubscriptionPeriod
  };
};

export const shouldDisableTeamMemberActions = ({
  isStripePayment,
  hasTeamOwnerEntitlement,
  hasTeamAdminEntitlement,
  entitlements,
  remainingLicenses,
  subscriberUserId,
  teamOwnerId
}: {
  isStripePayment: boolean;
  hasTeamOwnerEntitlement: boolean;
  hasTeamAdminEntitlement: boolean;
  entitlements: Entitlement[];
  remainingLicenses: number;
  subscriberUserId?: string;
  teamOwnerId?: string;
}) => {
  if (isStripePayment) {
    const isTeamOwner = subscriberUserId === teamOwnerId;
    if (isTeamOwner) {
      return hasTeamOwnerEntitlement
        ? checkSubscriptionValidity(
            entitlements.find(
              (entitle) => entitle.entitlement === ENTITLEMENT.TEAM_OWNER
            )
          ).hasActiveSubscription
        : true;
    } else {
      return hasTeamAdminEntitlement
        ? checkSubscriptionValidity(
            entitlements.find(
              (entitle) => entitle.entitlement === ENTITLEMENT.TEAM_ADMIN
            )
          ).hasActiveSubscription
        : true;
    }
  } else {
    return !remainingLicenses;
  }
};

export const isValidInvitationUserId = (
  user_id: string,
  teamMembers: TeamsMembers
) => ({
  isValidUserId: isUUID(user_id),
  isUserInTeam: [
    ...teamMembers.admins,
    ...teamMembers.members.map((member) => member.id)
  ].includes(user_id)
});

export const maskEmail = (email?: string) => {
  if (!email) {
    return EMPTY_STRING;
  }
  const [name, domain] = email.split('@');
  if (!name || !domain) {
    return '***@***';
  }

  const visiblePart = name.slice(0, 2);
  return `${visiblePart}****@${domain}`;
};

export const isDateBelowMin = (inputDate: string | Date) => {
  const minDate = moment().add(1, 'day').startOf('day');
  const dateToCheck = moment(inputDate).startOf('day');
  return dateToCheck.isBefore(minDate);
};

export const getInvitationStatusStyles: Record<InvitationStatus, string> = {
  [InvitationStatus.FAILED]: 'bg-red-600',
  [InvitationStatus.PENDING]: 'bg-gray-600',
  [InvitationStatus.ACCEPTED]: 'bg-green-600',
  [InvitationStatus.DECLINED]: 'bg-yellow-600',
  [InvitationStatus.EXPIRED]: 'bg-neutral-500'
};

/** i18n label key per invitation status (resolve with `t(...)` at the call site). */
export const INVITATION_STATUS_LABEL_KEY: Record<InvitationStatus, string> = {
  [InvitationStatus.FAILED]: 'admin_team.failed',
  [InvitationStatus.PENDING]: 'admin_team.pending',
  [InvitationStatus.ACCEPTED]: 'admin_team.accepted',
  [InvitationStatus.DECLINED]: 'admin_team.declined',
  [InvitationStatus.EXPIRED]: 'admin_team.expired'
};

export const EmptyColumnPlaceholder = '—'.repeat(6);

/** Caps at 100% and formats with one decimal (e.g. `74.5%`). */
export const formatAvgRoutineFocusCompletionLabel = (value: number): string =>
  `${toFixedNumber(Math.min(MAX_COMPLETION_PERCENT, value))}%`;

export const filterMembersBySearchTerm = (
  members: TeamMember[],
  searchTerm: string
): TeamMember[] => {
  const loweredTerm = searchTerm?.toLowerCase() ?? EMPTY_STRING;
  return members.filter(
    (member) =>
      member.email?.toLowerCase()?.includes(loweredTerm) ||
      member.first_name?.toLowerCase()?.includes(loweredTerm) ||
      member.last_name?.toLowerCase()?.includes(loweredTerm)
  );
};

export const transformTeamMembersPayload = (payload: {
  admins?: string[];
  teachers?: string[];
  members?: TeamMember[];
}) => {
  return {
    admins: payload?.admins || [],
    teachers: payload?.teachers || [],
    members:
      payload?.members
        ?.map((member: TeamMember) => {
          const morning_percent_number_day_of_stats_completed =
            toInt(
              member.morning_percent_number_day_of_stats_completed?.toString()
            ) ?? 0;
          const evening_percent_number_day_of_stats_completed =
            toInt(
              member.evening_percent_number_day_of_stats_completed?.toString()
            ) ?? 0;
          const focus_modes_percent_number_day_of_stats_completed =
            toInt(
              member.focus_modes_percent_number_day_of_stats_completed?.toString()
            ) ?? 0;

          const total =
            morning_percent_number_day_of_stats_completed +
            evening_percent_number_day_of_stats_completed +
            focus_modes_percent_number_day_of_stats_completed;
          // Cap at 100% — backend focus_modes count can exceed 100% if a user
          // completes multiple focus modes per day. Frontend caps defensively
          // until the backend calculation is fixed to use "% of days with
          // at least 1 focus mode completed".
          const avgRoutineFocusCompletion90d = Math.min(
            MAX_COMPLETION_PERCENT,
            total / TOTAL_COMPLETION_CATEGORIES_90D
          );

          return {
            ...member,
            first_name: member?.first_name ?? EMPTY_STRING,
            last_name: member?.last_name ?? EMPTY_STRING,
            morning_percent_number_day_of_stats_completed,
            evening_percent_number_day_of_stats_completed,
            micro_percent_number_day_of_stats_completed:
              toInt(
                member?.micro_percent_number_day_of_stats_completed?.toString()
              ) ?? 0,
            avgRoutineFocusCompletion90d
          };
        })
        ?.sort(
          (a, b) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        ) ?? []
  };
};

/**
 * Semantic label slots whose wording swaps between generic team terminology
 * and school terminology (Team→School, member→student, admin→teacher) when a
 * team is flagged `is_school` — plan F5. Values are i18n keys: resolve them
 * with `t(...)` at the call site so labels follow the active language.
 */
export interface TeamTerminology {
  updateTeamName: string;
  noMembersFound: string;
  totalNumberOfMembers: string;
  totalNumberOfLicenses: string;
  removeMember: string;
  addMember: string;
  inviteMember: string;
  adminRole: string;
  standardRole: string;
}

const TEAM_TERMINOLOGY: TeamTerminology = {
  updateTeamName: 'admin_team.update_team_name',
  noMembersFound: 'admin_team.no_members_found',
  totalNumberOfMembers: 'admin_team.total_number_of_members',
  totalNumberOfLicenses: 'admin_team.total_number_of_licenses',
  removeMember: 'admin_team.remove_member',
  addMember: 'admin_team.add_member',
  inviteMember: 'admin_team.invite_member',
  adminRole: 'admin_team.admin',
  standardRole: 'admin_team.standard'
};

const SCHOOL_TERMINOLOGY: TeamTerminology = {
  updateTeamName: 'school.update_school_name',
  noMembersFound: 'school.no_students_found',
  totalNumberOfMembers: 'school.total_number_of_students',
  totalNumberOfLicenses: 'school.total_number_of_licenses',
  removeMember: 'school.remove_student',
  // Add/invite cover students AND teachers/admins, so they say "User"; the
  // admin slot must say "Admin" — "Teacher" is exclusively the stored
  // school-level teacher role (Part H), never a relabel of admins.
  addMember: 'school.add_existing_user',
  inviteMember: 'school.invite_user',
  adminRole: 'school.admin',
  standardRole: 'school.student'
};

export const getTeamTerminology = (
  team?: Pick<Team, 'is_school'> | null
): TeamTerminology =>
  team?.is_school === true ? SCHOOL_TERMINOLOGY : TEAM_TERMINOLOGY;

/**
 * True when any team in the list is flagged school. Used for class-management
 * fetches and school-aware subscription list titles.
 */
export const hasSchoolTeam = (
  teams?: Array<Pick<Team, 'is_school'> | null> | null
): boolean => (teams ?? []).some((team) => team?.is_school === true);

/**
 * Subscription label slots. Available plans always use team keys; pass a school
 * team list only when labelling an existing school subscription card.
 * Values are i18n keys — resolve with `t(...)`.
 */
export interface SubscriptionTerminology {
  planTab: string;
  planTitle: string;
  planDescription: string;
  addRemoveUsers: string;
  productiveYetPrivate: string;
  leaderBoard: string;
  planPostfix: string;
  focusBearSubscription: string;
}

const TEAM_SUBSCRIPTION_TERMINOLOGY: SubscriptionTerminology = {
  planTab: 'subscription_page.team',
  planTitle: 'subscription_page.team_subscription',
  planDescription: 'subscription_page.team_description',
  addRemoveUsers: 'subscription_page.add_remove_users',
  productiveYetPrivate: 'subscription_page.productive_yet_private',
  leaderBoard: 'subscription_page.team_leader_board',
  planPostfix: 'subscription_page.team_plan_postfix',
  focusBearSubscription: 'subscription_page.focus_bear_team_subscription'
};

const SCHOOL_SUBSCRIPTION_TERMINOLOGY: SubscriptionTerminology = {
  planTab: 'subscription_page.school',
  planTitle: 'subscription_page.school_subscription',
  planDescription: 'subscription_page.school_description',
  addRemoveUsers: 'subscription_page.school_add_remove_users',
  productiveYetPrivate: 'subscription_page.school_productive_yet_private',
  leaderBoard: 'subscription_page.school_classes_feature',
  planPostfix: 'subscription_page.school_plan_postfix',
  focusBearSubscription: 'subscription_page.focus_bear_school_subscription'
};

export const getSubscriptionTerminology = (
  teams?: Array<Pick<Team, 'is_school'> | null> | null
): SubscriptionTerminology =>
  hasSchoolTeam(teams)
    ? SCHOOL_SUBSCRIPTION_TERMINOLOGY
    : TEAM_SUBSCRIPTION_TERMINOLOGY;

/** Trimmed rename value to submit, or `null` when empty/unchanged. */
export const getTeamRenameSubmission = (
  currentName: string | null | undefined,
  draft: string
): string | null => {
  const trimmed = draft.trim();
  if (!trimmed || trimmed === (currentName ?? '')) {
    return null;
  }
  return trimmed;
};

/**
 * A member's access can't outlast the team, so the invite form's default expiry
 * (`YYYY-MM-DD`) is capped at the team's own expiry date.
 */
export const clampMemberExpiryToTeam = (
  memberExpiry: string,
  teamExpiresDate?: string | null
): string => {
  if (!teamExpiresDate || Number.isNaN(Date.parse(teamExpiresDate))) {
    return memberExpiry;
  }
  const teamExpiry = new Date(teamExpiresDate).toISOString().slice(0, 10);
  return teamExpiry < memberExpiry ? teamExpiry : memberExpiry;
};
